import { useEffect, useState } from 'react';
import { Box, Button, Typography, Alert, Snackbar } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Layout } from '../../components/Layout/Layout';
import { UserTable } from '../../components/UserTable/UserTable';
import { CreateUserDialog } from '../../components/CreateUserDialog/CreateUserDialog';
import { DeleteUserDialog } from '../../components/DeleteUserDialog/DeleteUserDialog';
import { SetPasswordDialog } from '../../components/SetPasswordDialog/SetPasswordDialog';
import { useUsers } from '../../hooks/useUsers';
import { useAuthContext } from '../../hooks/useAuth';
import { CreateUserRequest, SetPasswordRequest } from '../../types';

export function Users(): JSX.Element {
  const { user } = useAuthContext();
  const currentUserIsRoot = user?.isRoot ?? false;

  const {
    users,
    isLoading,
    error,
    hasMore,
    togglingAdminUsername,
    togglingEnabledUsername,
    fetchUsers,
    createUser,
    deleteUser,
    setUserPassword,
    toggleAdmin,
    toggleEnabled,
    clearError,
  } = useUsers();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteUsername, setDeleteUsername] = useState<string | null>(null);
  const [passwordUsername, setPasswordUsername] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    fetchUsers(true);
  }, []);

  const handleRefresh = (): void => {
    fetchUsers(true);
  };

  const handleLoadMore = (): void => {
    if (hasMore && !isLoading) {
      fetchUsers(false);
    }
  };

  const handleCreateUser = async (request: CreateUserRequest): Promise<void> => {
    await createUser(request);
    setSuccessMessage(`User "${request.username}" created successfully`);
  };

  const handleDeleteUser = async (username: string): Promise<void> => {
    await deleteUser(username);
    setSuccessMessage(`User "${username}" deleted successfully`);
  };

  const handleSetPassword = async (
    username: string,
    request: SetPasswordRequest
  ): Promise<void> => {
    await setUserPassword(username, request);
    setSuccessMessage(`Password for "${username}" updated successfully`);
  };

  const handleToggleAdmin = async (username: string): Promise<void> => {
    try {
      const result = await toggleAdmin(username);
      const message = result.isAdmin
        ? `User "${username}" is now an administrator`
        : `User "${username}" is no longer an administrator`;
      setSuccessMessage(message);
    } catch {
      // Error is already handled by the hook
    }
  };

  const handleToggleEnabled = async (username: string): Promise<void> => {
    try {
      const result = await toggleEnabled(username);
      const message = result.enabled
        ? `User "${username}" is now enabled`
        : `User "${username}" is now disabled`;
      setSuccessMessage(message);
    } catch {
      // Error is already handled by the hook
    }
  };

  return (
    <Layout>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Users
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={isLoading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create User
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      <UserTable
        users={users}
        isLoading={isLoading}
        togglingAdminUsername={togglingAdminUsername}
        togglingEnabledUsername={togglingEnabledUsername}
        currentUserIsRoot={currentUserIsRoot}
        onDelete={(username) => setDeleteUsername(username)}
        onSetPassword={(username) => setPasswordUsername(username)}
        onToggleAdmin={handleToggleAdmin}
        onToggleEnabled={handleToggleEnabled}
      />

      {hasMore && users.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <Button onClick={handleLoadMore} disabled={isLoading}>
            {isLoading ? 'Loading...' : 'Load More'}
          </Button>
        </Box>
      )}

      <CreateUserDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateUser}
      />

      <DeleteUserDialog
        open={!!deleteUsername}
        username={deleteUsername}
        onClose={() => setDeleteUsername(null)}
        onConfirm={handleDeleteUser}
      />

      <SetPasswordDialog
        open={!!passwordUsername}
        username={passwordUsername}
        onClose={() => setPasswordUsername(null)}
        onSubmit={handleSetPassword}
      />

      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
      />
    </Layout>
  );
}
